import React from "react";
import Input from "../../shared/Input/Input";
import CustomDatePickerV2 from "shared/DateField/CustomDatePickerV2";
import { format } from "date-fns";
import moment from "moment";

interface FormFieldProps {
  label: string;
  name: string;
  type?:
    | "text"
    | "email"
    | "date"
    | "checkbox"
    | "radio"
    | "select"
    | "textarea"
    | "password";
  value: any; // Adjusted to handle checkbox, radio, and date types
  onChange: (event: any) => void;
  onBlur: (
    event: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => void;
  error?: any;
  options?: Array<{ value: string; label: string }>; // For select and radio inputs
  rowIntextArea?: number;
  disabled?: boolean;
  placeholder?: string;
  isCrossRequired?: boolean;
  isDOB?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const FormField: React.FC<FormFieldProps> = ({
  label = "",
  name,
  type = "text",
  value,
  placeholder = "",
  onChange,
  onBlur,
  error,
  options = [],
  rowIntextArea = 3,
  disabled = false,
  minDate,
  maxDate,
}) => {
  const handleChange = (e: any) => {
    if (e && typeof e === "object" && "target" in e) {
      onChange(e);
    } else {
      onChange({ name, value: e });
    }
  };

  return (
    <div className="w-full">
      {/* Label */}
      <label
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        htmlFor={name}
      >
        {label}
      </label>

      {/* Select Input */}
      {type === "select" && (
        <select
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          className={`block w-full border rounded-2xl px-4 py-3 h-11 text-sm
            bg-white dark:bg-neutral-900
            border-neutral-200 dark:border-neutral-700
            text-gray-900 dark:text-white
            focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50
            dark:focus:ring-primary-600 dark:focus:ring-opacity-25
            disabled:bg-gray-100 dark:disabled:bg-neutral-800 disabled:cursor-not-allowed
            transition-colors
            ${error ? 'border-red-300 dark:border-red-700' : ''}`}
        >
          <option value="" disabled>
            Select
          </option>
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {/* Textarea Input */}
      {type === "textarea" && (
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          rows={rowIntextArea}
          placeholder={placeholder}
          className={`block w-full border rounded-2xl px-4 py-3 text-sm
            bg-white dark:bg-neutral-900
            border-neutral-200 dark:border-neutral-700
            text-gray-900 dark:text-white
            placeholder-gray-400 dark:placeholder-gray-500
            focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50
            dark:focus:ring-primary-600 dark:focus:ring-opacity-25
            disabled:bg-gray-100 dark:disabled:bg-neutral-800 disabled:cursor-not-allowed
            transition-colors resize-none
            ${error ? 'border-red-300 dark:border-red-700' : ''}`}
        />
      )}

      {/* Date Input */}
      {type === "date" && (
        <CustomDatePickerV2
          placeholder={`Select ${label}`}
          value={value || null}
          onChange={onChange}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
        />
      )}

      {/* Checkbox Input */}
      {type === "checkbox" && (
        <div className="flex items-center">
          <input
            id={name}
            name={name}
            type="checkbox"
            checked={value}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed"
          />
          <label htmlFor={name} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {label}
          </label>
        </div>
      )}

      {/* Radio Input */}
      {type === "radio" && options && (
        <div className="space-y-2">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center cursor-pointer"
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={handleChange}
                onBlur={onBlur}
                disabled={disabled}
                className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Text/Email/Password Input */}
      {(type === "text" || type === "email" || type === "password") && (
        <Input
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
        />
      )}

      {/* Error Message - FIXED WITH CONSISTENT TAILWIND STYLING */}
      {error && (
        <p className="mt-1.5 text-xs sm:text-sm text-red-600 dark:text-red-400 font-medium flex items-start gap-1.5">
          <svg
            className="w-4 h-4 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};

export default FormField;